/**
 * Blademaster kit: a signature clip for every ability (Arms warrior, dual swords, reds).
 *
 * Palette: crimson heads, blood-red / dark-crimson cores, red-orange for the fast builders, a
 * gold accent on Colossus Smash. Every clip has its own silhouette:
 *
 *   Mortal Strike   two swords raised behind the head, a stepping overhead double chop (big crescent)
 *   Overpower       blades flared up and out, then a snapping X cross-slash (twin red-orange streaks)
 *   Cleave          coil right, then a 270° horizontal sweep inside a full body turn (long red arc)
 *   Execute         crouch, leap up, overhead slam into the ground (dark-crimson smear, cracks)
 *   Bladestorm      continuous fast whirl with leaning, bobbing blades (overlapping red vortex)
 *   Colossus Smash  deep wind-up crouch, a spinning jump and a two-sword ground pound (red-gold rings)
 *   Pummel          cocked elbow, a short pommel jab (white flash)
 *   Die by the Sword  twirling blades in front form a red shield disc, ending in a crossed guard
 *   Charge          snap into a low forward lean with the blades streaming behind (red speed streaks)
 *   Slam            off-hand guard levelled, right blade hammered straight down (red-orange)
 *   Rend            blades crossed at the hip, ripped apart outward (blood smear + droplets)
 *   Ravager's Finale  two airborne counter-clockwise buzzsaw turns, a crouched double cleave landing
 *
 * Rules kept by every clip (checked in blademaster.test.ts):
 *  - root offsets (lunge / hop) end at 0, so the body always returns to the sim position;
 *  - spins and hand twirls end on whole turns and the following keys keep that value, so a
 *    recovery or blend-out never unwinds a turn (wrapPi makes a whole turn ≡ 0);
 *  - trail colours are reds (Colossus Smash adds gold).
 *
 * Registration: `registerBlademasterKit()` is called from classes/blademaster.ts and overrides the
 * framework's reference Mortal Strike / Bladestorm clips.
 */
import { ABILITY } from '@keystone/content';
import { type Clip, type CueDef, type PoseDef, type TrailDef, defineClip } from '../clips';
import { registerAbilityClips } from '../registry';

// --- palette ---------------------------------------------------------------------------------
const CRIMSON = '#ff2238';
const BLOOD = '#c4081c';
const DARK_CRIMSON = '#5e0010';
const CORE = '#2a0006';
const RED_ORANGE = '#ff5a1c';
const EMBER = '#a3200a';
const GOLD = '#ffb84a';
const WHITE = '#fff1ec';

/** One trail definition on both blades. */
const both = (t: Omit<TrailDef, 'hand'>): TrailDef => ({ ...t, hand: 'both' });

/** Same cue on both blade tips. */
const tips = (t: number, fx: CueDef['fx'], scale: number, color?: string): CueDef[] => [
  { t, fx, at: 'tip', hand: 'right', scale, ...(color ? { color } : {}) },
  { t, fx, at: 'tip', hand: 'left', scale: scale * 0.85, ...(color ? { color } : {}) },
];

// =============================================================================================
// Mortal Strike: both swords hauled up behind the head, a step, a crushing double chop.
// =============================================================================================
const MS_RAISED: PoseDef = {
  spine: [-0.2, -0.12, 0],
  chest: [-0.1, -0.05, 0],
  head: [0.05, 0.1, 0],
  rightUpperArm: [2.55, 0.05, 0.45],
  leftUpperArm: [2.55, 0.05, 0.45],
  rightLowerArm: [0.85],
  leftLowerArm: [0.85],
  leftUpperLeg: [0.25, 0, 0.08],
  rightUpperLeg: [-0.08, 0, 0.08],
  leftLowerLeg: [0.35],
  rightLowerLeg: [0.4],
};

export const mortalStrike = defineClip({
  id: 'blademaster.mortal_strike',
  duration: 0.62,
  blendIn: 0.04,
  keys: [
    // Anticipation: haul both blades up and back, lean back, weight on the back foot.
    { t: 0.13, ease: 'out', pose: MS_RAISED, root: { lunge: -0.1, hop: -0.02 } },
    // A held beat of tension: the coil creeps a touch further.
    {
      t: 0.19,
      ease: 'linear',
      pose: { ...MS_RAISED, spine: [-0.26, -0.14, 0], rightUpperArm: [2.7, 0.05, 0.5], leftUpperArm: [2.7, 0.05, 0.5], rightLowerArm: [1.0], leftLowerArm: [1.0] },
      root: { lunge: -0.12, hop: -0.03 },
    },
    // Impact: step in, fold over the chop, both blades driven down in front. Freeze.
    {
      t: 0.26,
      ease: 'snap',
      hold: 0.07,
      pose: {
        spine: [0.52, 0.06, 0],
        chest: [0.16, 0, 0],
        head: [-0.28, 0, 0],
        rightUpperArm: [0.62, 0.38, 0.12],
        leftUpperArm: [0.62, 0.38, 0.12],
        rightLowerArm: [0.04],
        leftLowerArm: [0.04],
        leftUpperLeg: [0.65, 0, 0.08],
        rightUpperLeg: [-0.35, 0, 0.08],
        leftLowerLeg: [0.7],
        rightLowerLeg: [0.45],
        leftFoot: [-0.15],
      },
      root: { lunge: 0.42, hop: -0.08 },
    },
    // Follow-through settles a touch lower before the recovery.
    {
      t: 0.4,
      ease: 'out',
      pose: {
        spine: [0.4, 0.04, 0],
        chest: [0.1, 0, 0],
        head: [-0.18, 0, 0],
        rightUpperArm: [0.45, 0.32, 0.18],
        leftUpperArm: [0.45, 0.32, 0.18],
        rightLowerArm: [0.15],
        leftLowerArm: [0.15],
        leftUpperLeg: [0.5, 0, 0.08],
        rightUpperLeg: [-0.25, 0, 0.08],
        leftLowerLeg: [0.55],
        rightLowerLeg: [0.35],
      },
      root: { lunge: 0.34, hop: -0.05 },
    },
    { t: 0.62, ease: 'out' },
  ],
  trails: [
    both({ from: 0.13, to: 0.31, color: CRIMSON, tail: DARK_CRIMSON, style: 'crescent', width: 1, fade: 0.3, alpha: 0.95 }),
    // Dark-crimson core under the glow (reads on bright floors too).
    both({ from: 0.15, to: 0.31, color: BLOOD, tail: CORE, style: 'crescent', width: 0.5, fade: 0.24, alpha: 0.55, blend: 'alpha' }),
  ],
  cues: [
    ...tips(0.26, 'slash', 1.35),
    { t: 0.26, fx: 'impact', at: 'front', scale: 1.1 },
    { t: 0.27, fx: 'shockwave', at: 'ground', scale: 0.85 },
    { t: 0.27, fx: 'cracks', at: 'ground', scale: 0.6, color: DARK_CRIMSON },
  ],
  hitStop: { t: 0.26, ms: 70, shake: 0.22 },
});

// =============================================================================================
// Overpower: blades flared up and out, then a snapping X across the front.
// =============================================================================================
export const overpower = defineClip({
  id: 'blademaster.overpower',
  duration: 0.46,
  blendIn: 0.03,
  keys: [
    // Flare: both blades up and out in a V, chest open, a small hop back.
    {
      t: 0.08,
      ease: 'out',
      pose: {
        spine: [-0.14, 0, 0],
        chest: [-0.1, 0, 0],
        head: [0.06, 0, 0],
        rightUpperArm: [1.85, -0.45, 1.05],
        leftUpperArm: [1.75, -0.45, 1.0],
        rightLowerArm: [0.5],
        leftLowerArm: [0.55],
        rightUpperLeg: [0.1],
        rightLowerLeg: [0.3],
        leftLowerLeg: [0.2],
      },
      root: { lunge: -0.06, hop: 0.03 },
    },
    // The X: both arms rip down and across each other, a darting step in. Brief freeze.
    {
      t: 0.15,
      ease: 'snap',
      hold: 0.04,
      pose: {
        spine: [0.34, 0.1, 0],
        chest: [0.1, 0, 0],
        head: [-0.18, -0.08, 0],
        rightUpperArm: [0.62, 1.2, 0.05],
        leftUpperArm: [0.52, 1.3, 0.0],
        rightLowerArm: [0.08],
        leftLowerArm: [0.12],
        leftUpperLeg: [0.5, 0, 0.06],
        rightUpperLeg: [-0.3, 0, 0.06],
        leftLowerLeg: [0.5],
        rightLowerLeg: [0.4],
      },
      root: { lunge: 0.3, hop: -0.04 },
    },
    {
      t: 0.28,
      ease: 'out',
      pose: {
        spine: [0.24, 0.05, 0],
        head: [-0.1, 0, 0],
        rightUpperArm: [0.4, 0.9, 0.3],
        leftUpperArm: [0.35, 0.95, 0.25],
        rightLowerArm: [0.25],
        leftLowerArm: [0.25],
        leftUpperLeg: [0.35],
        leftLowerLeg: [0.35],
        rightLowerLeg: [0.3],
      },
      root: { lunge: 0.22, hop: -0.02 },
    },
    { t: 0.46, ease: 'out' },
  ],
  trails: [
    // Right a hair earlier than left: the two streaks cross as an X instead of one smear.
    { from: 0.06, to: 0.2, hand: 'right', color: RED_ORANGE, tail: EMBER, style: 'streak', width: 0.95, fade: 0.2, alpha: 0.95 },
    { from: 0.075, to: 0.21, hand: 'left', color: RED_ORANGE, tail: EMBER, style: 'streak', width: 0.95, fade: 0.2, alpha: 0.95 },
    both({ from: 0.07, to: 0.2, color: '#ff7a3a', tail: BLOOD, style: 'crescent', width: 0.35, fade: 0.14, alpha: 0.6 }),
  ],
  cues: [...tips(0.15, 'slash', 0.95), { t: 0.15, fx: 'sparks', at: 'front', scale: 0.8 }],
  hitStop: { t: 0.15, ms: 38, shake: 0.1 },
});

// =============================================================================================
// Cleave: coil right, then one full body turn; the blades sweep a 270° arc at shoulder height.
// =============================================================================================
const CLEAVE_SWEEP_ARMS: PoseDef = {
  rightUpperArm: [0.6, 0.35, 1.1],
  leftUpperArm: [0.1, -0.2, 0.85],
  rightLowerArm: [0.25],
  leftLowerArm: [0.5],
  rightHand: [0, 0, 0.25],
};
const CLEAVE_LEGS: PoseDef = {
  leftUpperLeg: [0.3, 0, 0.22],
  rightUpperLeg: [0.3, 0, 0.22],
  leftLowerLeg: [0.55],
  rightLowerLeg: [0.55],
};

export const cleave = defineClip({
  id: 'blademaster.cleave',
  duration: 0.68,
  blendIn: 0.04,
  keys: [
    // Coil: torso twisted hard right, right blade drawn back across, low wide stance.
    {
      t: 0.13,
      ease: 'out',
      pose: {
        spine: [0.1, -0.62, 0],
        chest: [0, -0.28, 0],
        head: [0, 0.6, 0],
        rightUpperArm: [0.45, -0.55, 0.95],
        leftUpperArm: [0.7, 0.95, 0.3],
        rightLowerArm: [0.6],
        leftLowerArm: [1.1],
        ...CLEAVE_LEGS,
      },
      root: { hop: -0.06, spin: -0.07 },
    },
    // Release: the torso unwinds and the body whips round.
    {
      t: 0.24,
      ease: 'snap',
      pose: { spine: [0.16, 0.38, 0.08], chest: [0.04, 0.15, 0], head: [-0.05, -0.2, 0], ...CLEAVE_SWEEP_ARMS, ...CLEAVE_LEGS },
      root: { hop: -0.03, spin: 0.45 },
    },
    {
      t: 0.33,
      ease: 'linear',
      pose: { spine: [0.2, 0.5, 0.12], chest: [0.05, 0.2, 0], head: [-0.05, -0.25, 0], ...CLEAVE_SWEEP_ARMS, ...CLEAVE_LEGS },
      root: { hop: -0.04, spin: 0.84 },
    },
    // End of the arc: blade wrapped across the front, full turn completed. Freeze.
    {
      t: 0.4,
      ease: 'out',
      hold: 0.05,
      pose: {
        spine: [0.26, 0.58, 0],
        chest: [0.08, 0.2, 0],
        head: [-0.1, -0.35, 0],
        rightUpperArm: [0.55, 1.05, 0.55],
        leftUpperArm: [0.3, -0.25, 1.0],
        rightLowerArm: [0.15],
        leftLowerArm: [0.3],
        ...CLEAVE_LEGS,
      },
      root: { hop: -0.06, spin: 1 },
    },
    // Recovery keeps the whole turn (≡ 0) so nothing unwinds.
    { t: 0.68, ease: 'out', root: { spin: 1 } },
  ],
  trails: [
    { from: 0.13, to: 0.43, hand: 'right', color: CRIMSON, tail: DARK_CRIMSON, style: 'crescent', width: 1, fade: 0.36, alpha: 0.95 },
    { from: 0.15, to: 0.43, hand: 'left', color: BLOOD, tail: CORE, style: 'crescent', width: 0.75, fade: 0.28, alpha: 0.6 },
    { from: 0.16, to: 0.42, hand: 'right', color: BLOOD, tail: CORE, style: 'crescent', width: 0.45, fade: 0.26, alpha: 0.45, blend: 'alpha' },
  ],
  cues: [
    { t: 0.3, fx: 'slash', at: 'front', scale: 1.5 },
    { t: 0.3, fx: 'sparks', at: 'tip', hand: 'right', scale: 0.8 },
    { t: 0.4, fx: 'shockwave', at: 'self', scale: 0.9 },
    { t: 0.41, fx: 'dust', at: 'ground', scale: 0.8 },
  ],
  hitStop: { t: 0.3, ms: 45, shake: 0.15 },
});

// =============================================================================================
// Execute: crouch, leap, overhead slam. Dark-crimson smear, ground cracks, long freeze.
// =============================================================================================
export const execute = defineClip({
  id: 'blademaster.execute',
  duration: 0.84,
  blendIn: 0.04,
  keys: [
    // Crouch: swords low and back, weight loaded.
    {
      t: 0.14,
      ease: 'out',
      pose: {
        spine: [0.38, 0, 0],
        head: [-0.25, 0, 0],
        rightUpperArm: [-0.45, 0, 0.45],
        leftUpperArm: [-0.45, 0, 0.45],
        rightLowerArm: [0.3],
        leftLowerArm: [0.3],
        leftUpperLeg: [0.55, 0, 0.12],
        rightUpperLeg: [0.55, 0, 0.12],
        leftLowerLeg: [0.95],
        rightLowerLeg: [0.95],
        leftFoot: [-0.35],
        rightFoot: [-0.35],
      },
      root: { hop: -0.09, lunge: -0.05 },
    },
    // Apex: up in the air, arched back, both blades high overhead, legs tucked.
    {
      t: 0.32,
      ease: 'out',
      pose: {
        spine: [-0.3, 0, 0],
        chest: [-0.12, 0, 0],
        head: [0.1, 0, 0],
        rightUpperArm: [2.75, 0.1, 0.35],
        leftUpperArm: [2.75, 0.1, 0.35],
        rightLowerArm: [0.7],
        leftLowerArm: [0.7],
        leftUpperLeg: [0.75, 0, 0.1],
        rightUpperLeg: [0.35, 0, 0.1],
        leftLowerLeg: [1.2],
        rightLowerLeg: [1.0],
      },
      root: { hop: 0.75, lunge: 0.25 },
    },
    // Slam: drive both blades into the ground, kneel into it. Long freeze.
    {
      t: 0.4,
      ease: 'snap',
      hold: 0.1,
      pose: {
        spine: [0.65, 0, 0],
        chest: [0.2, 0, 0],
        head: [-0.3, 0, 0],
        rightUpperArm: [0.4, 0.42, 0.08],
        leftUpperArm: [0.4, 0.42, 0.08],
        rightLowerArm: [0],
        leftLowerArm: [0],
        leftUpperLeg: [0.8, 0, 0.1],
        rightUpperLeg: [-0.05, 0, 0.1],
        leftLowerLeg: [1.0],
        rightLowerLeg: [1.15],
        rightFoot: [0.3],
      },
      root: { hop: -0.1, lunge: 0.5 },
    },
    {
      t: 0.62,
      ease: 'out',
      pose: {
        spine: [0.35, 0, 0],
        head: [-0.15, 0, 0],
        rightUpperArm: [0.35, 0.3, 0.3],
        leftUpperArm: [0.35, 0.3, 0.3],
        rightLowerArm: [0.2],
        leftLowerArm: [0.2],
        leftUpperLeg: [0.4, 0, 0.1],
        rightUpperLeg: [0.05, 0, 0.1],
        leftLowerLeg: [0.5],
        rightLowerLeg: [0.55],
      },
      root: { hop: -0.04, lunge: 0.3 },
    },
    { t: 0.84, ease: 'out' },
  ],
  trails: [
    // Dark-crimson smear (alpha blend) with a thin glowing edge on top.
    both({ from: 0.2, to: 0.46, color: DARK_CRIMSON, tail: CORE, style: 'crescent', width: 1, fade: 0.34, alpha: 0.9, blend: 'alpha' }),
    both({ from: 0.24, to: 0.46, color: CRIMSON, tail: DARK_CRIMSON, style: 'crescent', width: 0.4, fade: 0.2, alpha: 0.9 }),
  ],
  cues: [
    { t: 0.4, fx: 'impact', at: 'tip', hand: 'right', scale: 1.3 },
    { t: 0.4, fx: 'cracks', at: 'ground', scale: 1.25, color: DARK_CRIMSON },
    { t: 0.4, fx: 'shockwave', at: 'ground', scale: 1.05 },
    { t: 0.41, fx: 'dust', at: 'ground', scale: 1.1 },
    { t: 0.42, fx: 'embers', at: 'ground', scale: 0.8, color: CRIMSON },
  ],
  hitStop: { t: 0.4, ms: 95, shake: 0.35 },
});

// =============================================================================================
// Bladestorm (channel loop): a fast continuous whirl. The blades ride at different heights and
// swap every half turn, so the two ribbons weave a vortex; the body leans into the spin.
// =============================================================================================
const BS_LEGS: PoseDef = {
  leftUpperLeg: [0.32, 0, 0.26],
  rightUpperLeg: [0.32, 0, 0.26],
  leftLowerLeg: [0.6],
  rightLowerLeg: [0.6],
};
const bsPose = (hi: 'right' | 'left', lean: number): PoseDef => ({
  spine: [0.22, 0, lean],
  chest: [0.05, 0, lean * 0.5],
  head: [-0.12, 0, -lean * 0.6],
  rightUpperArm: hi === 'right' ? [0.7, 0.05, 1.3] : [0.05, 0.05, 1.0],
  leftUpperArm: hi === 'left' ? [0.7, 0.05, 1.3] : [0.05, 0.05, 1.0],
  rightLowerArm: [0.1],
  leftLowerArm: [0.1],
  ...BS_LEGS,
});

export const bladestorm = defineClip({
  id: 'blademaster.bladestorm',
  // One turn every 0.38 s (≈ 2.6 turns/s) after a 0.16 s wind-in.
  duration: 0.54,
  loop: true,
  loopFrom: 0.16,
  blendIn: 0.06,
  mask: 'full',
  keys: [
    // Wind-in: drop into the stance, arms flung out, a little pre-turn.
    { t: 0.16, ease: 'out', pose: bsPose('right', 0.12), root: { hop: -0.08, spin: 0 } },
    { t: 0.35, ease: 'linear', pose: bsPose('left', 0.16), root: { hop: -0.03, spin: 0.5 } },
    { t: 0.54, ease: 'linear', pose: bsPose('right', 0.12), root: { hop: -0.08, spin: 1 } },
  ],
  // Windows span the loop: continuous ribbons for the whole channel.
  trails: [
    { from: 0.08, to: 0.54, hand: 'right', color: CRIMSON, tail: DARK_CRIMSON, style: 'crescent', width: 1, fade: 0.3, alpha: 0.8 },
    { from: 0.08, to: 0.54, hand: 'left', color: RED_ORANGE, tail: BLOOD, style: 'crescent', width: 1, fade: 0.3, alpha: 0.75 },
    { from: 0.1, to: 0.54, hand: 'right', color: BLOOD, tail: CORE, style: 'crescent', width: 0.4, fade: 0.18, alpha: 0.4, blend: 'alpha' },
  ],
  cues: [
    { t: 0.16, fx: 'dust', at: 'ground', scale: 0.55 },
    { t: 0.3, fx: 'sparks', at: 'tip', hand: 'right', scale: 0.45 },
    { t: 0.46, fx: 'sparks', at: 'tip', hand: 'left', scale: 0.45 },
  ],
});

// =============================================================================================
// Colossus Smash: deep wind-up crouch, a spinning jump, a two-sword ground pound (red-gold).
// =============================================================================================
const CS_CROUCH: PoseDef = {
  spine: [0.5, 0, 0],
  chest: [0.12, 0, 0],
  head: [-0.4, 0, 0],
  rightUpperArm: [-0.55, 0.15, 0.6],
  leftUpperArm: [-0.55, 0.15, 0.6],
  rightLowerArm: [0.25],
  leftLowerArm: [0.25],
  leftUpperLeg: [0.6, 0, 0.26],
  rightUpperLeg: [0.6, 0, 0.26],
  leftLowerLeg: [1.05],
  rightLowerLeg: [1.05],
  leftFoot: [-0.4],
  rightFoot: [-0.4],
};

export const colossusSmash = defineClip({
  id: 'blademaster.colossus_smash',
  duration: 0.95,
  blendIn: 0.05,
  keys: [
    // Wind-up: sink into a deep crouch, blades swept low behind.
    { t: 0.2, ease: 'out', pose: CS_CROUCH, root: { hop: -0.1 } },
    // Coiled beat: a little deeper (the build-up the hit pays off).
    { t: 0.28, ease: 'inOut', pose: { ...CS_CROUCH, spine: [0.56, -0.12, 0], leftLowerArm: [0.35], rightLowerArm: [0.35] }, root: { hop: -0.12, spin: -0.04 } },
    // Launch: spring up turning, blades windmilling overhead.
    {
      t: 0.47,
      ease: 'out',
      pose: {
        spine: [-0.32, 0, 0],
        chest: [-0.14, 0, 0],
        head: [0.12, 0, 0],
        rightUpperArm: [2.85, 0, 0.55],
        leftUpperArm: [2.85, 0, 0.55],
        rightLowerArm: [0.45],
        leftLowerArm: [0.45],
        leftUpperLeg: [0.85, 0, 0.12],
        rightUpperLeg: [0.85, 0, 0.12],
        leftLowerLeg: [1.35],
        rightLowerLeg: [1.35],
      },
      root: { hop: 0.95, spin: 0.62 },
    },
    // Ground pound: complete the turn, both blades hammered into the floor, wide squat. Long freeze.
    {
      t: 0.55,
      ease: 'snap',
      hold: 0.12,
      pose: {
        spine: [0.78, 0, 0],
        chest: [0.22, 0, 0],
        head: [-0.32, 0, 0],
        rightUpperArm: [0.22, 0.5, 0.06],
        leftUpperArm: [0.22, 0.5, 0.06],
        rightLowerArm: [0],
        leftLowerArm: [0],
        leftUpperLeg: [0.7, 0, 0.32],
        rightUpperLeg: [0.7, 0, 0.32],
        leftLowerLeg: [1.15],
        rightLowerLeg: [1.15],
        leftFoot: [-0.3],
        rightFoot: [-0.3],
      },
      root: { hop: -0.12, spin: 1, lunge: 0.12 },
    },
    {
      t: 0.78,
      ease: 'out',
      pose: {
        spine: [0.4, 0, 0],
        head: [-0.15, 0, 0],
        rightUpperArm: [0.3, 0.35, 0.3],
        leftUpperArm: [0.3, 0.35, 0.3],
        rightLowerArm: [0.2],
        leftLowerArm: [0.2],
        leftUpperLeg: [0.35, 0, 0.15],
        rightUpperLeg: [0.35, 0, 0.15],
        leftLowerLeg: [0.55],
        rightLowerLeg: [0.55],
      },
      root: { hop: -0.05, spin: 1, lunge: 0.06 },
    },
    { t: 0.95, ease: 'out', root: { spin: 1 } },
  ],
  trails: [
    { from: 0.26, to: 0.6, hand: 'right', color: CRIMSON, tail: GOLD, style: 'crescent', width: 1, fade: 0.32, alpha: 0.9 },
    { from: 0.26, to: 0.6, hand: 'left', color: GOLD, tail: BLOOD, style: 'crescent', width: 1, fade: 0.32, alpha: 0.85 },
    both({ from: 0.3, to: 0.6, color: BLOOD, tail: CORE, style: 'crescent', width: 0.45, fade: 0.24, alpha: 0.45, blend: 'alpha' }),
  ],
  cues: [
    { t: 0.2, fx: 'dust', at: 'self', scale: 0.5 },
    { t: 0.55, fx: 'impact', at: 'ground', scale: 1.3, color: CRIMSON },
    { t: 0.55, fx: 'shockwave', at: 'ground', scale: 1.7, color: GOLD },
    { t: 0.56, fx: 'shockwave', at: 'ground', scale: 1.15, color: CRIMSON },
    { t: 0.55, fx: 'cracks', at: 'ground', scale: 1.35, color: BLOOD },
    { t: 0.56, fx: 'dust', at: 'ground', scale: 1.4 },
    { t: 0.57, fx: 'embers', at: 'ground', scale: 1.0, color: GOLD },
  ],
  hitStop: { t: 0.55, ms: 110, shake: 0.45 },
});

// =============================================================================================
// Pummel (off-GCD interrupt): cock the elbow, jab the pommel forward. Tiny, fast, white flash.
// =============================================================================================
export const pummel = defineClip({
  id: 'blademaster.pummel',
  duration: 0.36,
  blendIn: 0.03,
  keys: [
    {
      t: 0.06,
      ease: 'out',
      pose: {
        spine: [0, -0.38, 0],
        chest: [0, -0.12, 0],
        head: [0, 0.35, 0],
        rightUpperArm: [0.55, -0.25, 0.55],
        rightLowerArm: [1.55],
        rightHand: [0, 0, 0.5],
      },
      root: { lunge: -0.04 },
    },
    {
      t: 0.11,
      ease: 'snap',
      hold: 0.04,
      pose: {
        spine: [0.14, 0.36, 0],
        chest: [0.05, 0.12, 0],
        head: [-0.05, -0.3, 0],
        rightUpperArm: [1.45, 0.4, 0.12],
        rightLowerArm: [0.05],
        rightHand: [0, 0, -0.6],
        leftUpperArm: [-0.2, 0, 0.2],
        leftUpperLeg: [0.32],
        leftLowerLeg: [0.3],
        rightLowerLeg: [0.2],
      },
      root: { lunge: 0.2 },
    },
    { t: 0.36, ease: 'out' },
  ],
  trails: [{ from: 0.05, to: 0.15, hand: 'right', color: WHITE, tail: CRIMSON, style: 'streak', width: 0.5, fade: 0.1, alpha: 0.75 }],
  cues: [
    { t: 0.11, fx: 'flash', at: 'hand', hand: 'right', scale: 0.85, color: WHITE },
    { t: 0.11, fx: 'impact', at: 'front', scale: 0.55, color: WHITE },
  ],
  hitStop: { t: 0.11, ms: 30, shake: 0.08 },
});

// =============================================================================================
// Die by the Sword (off-GCD defensive): blades twirl in front (hand roll) into a red shield disc,
// then lock into a crossed guard. The twirl ends on whole turns (≡ 0) and the clip has no
// blend-out, so nothing spins back.
// =============================================================================================
const DBTS_STANCE: PoseDef = {
  spine: [0.08, 0, 0],
  head: [-0.05, 0, 0],
  leftUpperLeg: [0.22, 0, 0.24],
  rightUpperLeg: [0.05, 0, 0.24],
  leftLowerLeg: [0.4],
  rightLowerLeg: [0.35],
};
const dbtsArms = (roll: number, cross: number): PoseDef => ({
  rightUpperArm: [1.15, 0.45 + cross, 0.4],
  leftUpperArm: [1.15, 0.45 + cross, 0.4],
  rightLowerArm: [0.75],
  leftLowerArm: [0.75],
  rightHand: [roll, 0, 0],
  leftHand: [roll, 0, 0],
});

export const dieByTheSword = defineClip({
  id: 'blademaster.die_by_the_sword',
  duration: 0.95,
  blendIn: 0.04,
  blendOut: 0,
  keys: [
    { t: 0.08, ease: 'out', pose: { ...DBTS_STANCE, ...dbtsArms(0.4, 0) }, root: { hop: -0.04, lunge: -0.04 } },
    // Two full twirls at constant speed: the tips trace discs in front of the body.
    { t: 0.28, ease: 'linear', pose: { ...DBTS_STANCE, ...dbtsArms(Math.PI, 0.1) }, root: { hop: -0.04, lunge: -0.05 } },
    { t: 0.48, ease: 'linear', pose: { ...DBTS_STANCE, ...dbtsArms(2 * Math.PI, 0.2) }, root: { hop: -0.04, lunge: -0.06 } },
    { t: 0.64, ease: 'out', pose: { ...DBTS_STANCE, ...dbtsArms(4 * Math.PI, 0.3) }, root: { hop: -0.05, lunge: -0.06 } },
    // Crossed guard: blades in an X before the chest. Freeze, then settle.
    {
      t: 0.7,
      ease: 'back',
      overshoot: 1.4,
      hold: 0.1,
      pose: {
        ...DBTS_STANCE,
        spine: [0.12, 0, 0],
        rightUpperArm: [1.05, 0.95, 0.25],
        leftUpperArm: [1.05, 0.95, 0.25],
        rightLowerArm: [0.85],
        leftLowerArm: [0.85],
        rightHand: [4 * Math.PI, 0, 0],
        leftHand: [4 * Math.PI, 0, 0],
      },
      root: { hop: -0.06, lunge: -0.08 },
    },
    // Ends on the base pose with the twirl at whole turns (≡ 0): no pop, no unwinding.
    { t: 0.95, ease: 'out', pose: { rightHand: [4 * Math.PI, 0, 0], leftHand: [4 * Math.PI, 0, 0] } },
  ],
  trails: [
    both({ from: 0.06, to: 0.68, color: CRIMSON, tail: DARK_CRIMSON, style: 'crescent', width: 0.9, fade: 0.26, alpha: 0.8 }),
    both({ from: 0.1, to: 0.68, color: BLOOD, tail: CORE, style: 'crescent', width: 0.4, fade: 0.2, alpha: 0.4, blend: 'alpha' }),
  ],
  cues: [
    { t: 0.7, fx: 'flash', at: 'self', scale: 0.9, color: CRIMSON },
    ...tips(0.7, 'sparks', 0.7),
  ],
});

// =============================================================================================
// Charge: snap into a low forward lean, blades streaming back. The sim dash moves the unit, so
// the clip is upper body only and adds no root motion.
// =============================================================================================
const CHARGE_LEAN: PoseDef = {
  spine: [0.7, 0, 0],
  chest: [0.2, 0, 0],
  head: [-0.42, 0, 0],
  rightUpperArm: [-0.95, -0.1, 0.4],
  leftUpperArm: [-0.95, -0.1, 0.4],
  rightLowerArm: [0.35],
  leftLowerArm: [0.35],
};

export const charge = defineClip({
  id: 'blademaster.charge',
  duration: 0.7,
  blendIn: 0.03,
  mask: 'upper',
  keys: [
    { t: 0.05, ease: 'out', pose: { spine: [-0.08, 0, 0], rightUpperArm: [0.3, 0, 0.35], leftUpperArm: [0.3, 0, 0.35], rightLowerArm: [0.5], leftLowerArm: [0.5] } },
    { t: 0.13, ease: 'snap', pose: CHARGE_LEAN },
    { t: 0.52, ease: 'linear', pose: { ...CHARGE_LEAN, spine: [0.76, 0, 0], rightUpperArm: [-1.0, -0.1, 0.45], leftUpperArm: [-1.0, -0.1, 0.45] } },
    { t: 0.7, ease: 'out' },
  ],
  trails: [
    both({ from: 0.1, to: 0.58, color: CRIMSON, tail: RED_ORANGE, style: 'streak', width: 1, fade: 0.26, alpha: 0.9 }),
    both({ from: 0.12, to: 0.58, color: BLOOD, tail: CORE, style: 'crescent', width: 0.35, fade: 0.2, alpha: 0.35, blend: 'alpha' }),
  ],
  cues: [
    { t: 0.06, fx: 'dust', at: 'ground', scale: 0.8 },
    { t: 0.13, fx: 'flash', at: 'self', scale: 0.5, color: CRIMSON },
    { t: 0.56, fx: 'sparks', at: 'front', scale: 0.7 },
  ],
});

// =============================================================================================
// Rotation rework: Slam (5), Rend (6) and Ravager's Finale (Bladestorm's transform).
// =============================================================================================

/**
 * Slam (cheap filler): a one-handed hammer blow. The off-hand blade stays levelled at the target
 * as a guard while the right blade is hauled up behind the head, then dropped straight down with
 * the body sinking into it. Quick, compact, red-orange.
 */
export const slam = defineClip({
  id: 'blademaster.slam',
  duration: 0.52,
  blendIn: 0.03,
  keys: [
    // Haul: right blade up behind the head, left blade pointed forward, up on the toes.
    {
      t: 0.1,
      ease: 'out',
      pose: {
        spine: [-0.16, -0.2, 0],
        chest: [-0.08, -0.08, 0],
        head: [0.05, 0.2, 0],
        rightUpperArm: [2.6, -0.1, 0.4],
        rightLowerArm: [1.0],
        leftUpperArm: [1.25, 0.35, 0.2],
        leftLowerArm: [0.2],
        leftHand: [0, -0.9, 0],
        rightUpperLeg: [0.15, 0, 0.08],
        rightLowerLeg: [0.3],
        leftFoot: [-0.2],
      },
      root: { hop: 0.06, lunge: -0.05 },
    },
    // The blow: straight down in front, the body drops with it. Short freeze.
    {
      t: 0.18,
      ease: 'snap',
      hold: 0.05,
      pose: {
        spine: [0.5, 0.15, 0],
        chest: [0.14, 0.05, 0],
        head: [-0.28, -0.1, 0],
        rightUpperArm: [0.85, 0.35, 0.1],
        rightLowerArm: [0.05],
        leftUpperArm: [0.35, -0.2, 0.55],
        leftLowerArm: [0.6],
        leftUpperLeg: [0.55, 0, 0.1],
        leftLowerLeg: [0.7],
        rightUpperLeg: [-0.15, 0, 0.1],
        rightLowerLeg: [0.6],
      },
      root: { lunge: 0.26, hop: -0.12 },
    },
    {
      t: 0.34,
      ease: 'out',
      pose: {
        spine: [0.3, 0.1, 0],
        head: [-0.15, 0, 0],
        rightUpperArm: [0.6, 0.3, 0.25],
        rightLowerArm: [0.2],
        leftUpperArm: [0.3, -0.1, 0.45],
        leftLowerArm: [0.5],
        leftUpperLeg: [0.35, 0, 0.1],
        leftLowerLeg: [0.4],
        rightLowerLeg: [0.35],
      },
      root: { lunge: 0.16, hop: -0.05 },
    },
    { t: 0.52, ease: 'out' },
  ],
  trails: [
    { from: 0.08, to: 0.24, hand: 'right', color: RED_ORANGE, tail: EMBER, style: 'crescent', width: 1, fade: 0.22, alpha: 0.9 },
    { from: 0.11, to: 0.23, hand: 'right', color: CRIMSON, tail: CORE, style: 'crescent', width: 0.4, fade: 0.16, alpha: 0.5, blend: 'alpha' },
  ],
  cues: [
    { t: 0.18, fx: 'slash', at: 'tip', hand: 'right', scale: 1.0 },
    { t: 0.18, fx: 'sparks', at: 'tip', hand: 'right', scale: 0.7, color: RED_ORANGE },
    { t: 0.19, fx: 'dust', at: 'ground', scale: 0.6 },
  ],
  hitStop: { t: 0.18, ms: 35, shake: 0.1 },
});

/**
 * Rend: a tearing scissor. Both blades cross low at the left hip, then rip outward in opposite
 * directions at waist height (the wound opens), leaving a dark blood smear with a thin crimson
 * edge and a spray of blood-red droplets.
 */
export const rend = defineClip({
  id: 'blademaster.rend',
  duration: 0.58,
  blendIn: 0.04,
  keys: [
    // Cross: both blades crossed in front of the left hip, torso wound left, knees bent.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [0.25, 0.45, 0],
        chest: [0.06, 0.18, 0],
        head: [-0.1, -0.4, 0],
        rightUpperArm: [0.55, 1.25, 0.05],
        leftUpperArm: [0.35, 0.9, 0.05],
        rightLowerArm: [0.55],
        leftLowerArm: [0.8],
        rightHand: [0, 0, 0.4],
        leftHand: [0, 0, 0.4],
        leftUpperLeg: [0.35, 0, 0.12],
        rightUpperLeg: [0.3, 0, 0.12],
        leftLowerLeg: [0.55],
        rightLowerLeg: [0.5],
      },
      root: { hop: -0.06, lunge: -0.04 },
    },
    // Rip: the blades tear apart, flung wide to both sides, the torso unwinding to the front.
    {
      t: 0.21,
      ease: 'snap',
      hold: 0.06,
      pose: {
        spine: [0.3, -0.12, 0],
        chest: [0.08, -0.05, 0],
        head: [-0.2, 0.1, 0],
        rightUpperArm: [0.75, -0.55, 1.15],
        leftUpperArm: [0.75, -0.55, 1.15],
        rightLowerArm: [0.05],
        leftLowerArm: [0.05],
        rightHand: [0, 0, -0.3],
        leftHand: [0, 0, -0.3],
        leftUpperLeg: [0.5, 0, 0.18],
        rightUpperLeg: [-0.1, 0, 0.18],
        leftLowerLeg: [0.6],
        rightLowerLeg: [0.45],
      },
      root: { lunge: 0.28, hop: -0.08 },
    },
    {
      t: 0.38,
      ease: 'out',
      pose: {
        spine: [0.18, 0, 0],
        head: [-0.1, 0, 0],
        rightUpperArm: [0.45, -0.3, 0.8],
        leftUpperArm: [0.45, -0.3, 0.8],
        rightLowerArm: [0.3],
        leftLowerArm: [0.3],
        leftUpperLeg: [0.3, 0, 0.1],
        leftLowerLeg: [0.35],
        rightLowerLeg: [0.3],
      },
      root: { lunge: 0.14, hop: -0.03 },
    },
    { t: 0.58, ease: 'out' },
  ],
  trails: [
    // Blood smear first (alpha blend, reads on the dark floor as a dark-red tear), crimson edge on top.
    both({ from: 0.1, to: 0.28, color: BLOOD, tail: CORE, style: 'crescent', width: 1, fade: 0.36, alpha: 0.85, blend: 'alpha' }),
    both({ from: 0.12, to: 0.27, color: CRIMSON, tail: DARK_CRIMSON, style: 'streak', width: 0.5, fade: 0.2, alpha: 0.85 }),
  ],
  cues: [
    ...tips(0.21, 'embers', 0.8, BLOOD),
    { t: 0.21, fx: 'slash', at: 'front', scale: 1.2, color: CRIMSON },
    { t: 0.22, fx: 'sparks', at: 'front', scale: 0.5, color: BLOOD },
  ],
  hitStop: { t: 0.21, ms: 40, shake: 0.1 },
});

/**
 * Ravager's Finale (Bladestorm's transform, the Wounds cash-out): the storm ends in one drilling
 * leap. Coil low, then two full counter-clockwise turns airborne travelling forward with both
 * blades flung out level (a red buzzsaw, the reverse of Bladestorm's clockwise whirl), landing in
 * a crouched double downward cleave with a crimson ring tearing outward.
 */
const FINALE_SAW: PoseDef = {
  spine: [0.15, 0, 0.12],
  head: [-0.1, 0, 0],
  rightUpperArm: [0.4, 0, 1.25],
  leftUpperArm: [0.4, 0, 1.25],
  rightLowerArm: [0.05],
  leftLowerArm: [0.05],
  leftUpperLeg: [0.7, 0, 0.12],
  rightUpperLeg: [0.45, 0, 0.12],
  leftLowerLeg: [1.1],
  rightLowerLeg: [0.9],
};

export const ravagersFinale = defineClip({
  id: 'blademaster.ravagers_finale',
  duration: 0.92,
  blendIn: 0.04,
  keys: [
    // Coil: low crouch, wound to the right, both blades swept back.
    {
      t: 0.14,
      ease: 'out',
      pose: {
        spine: [0.35, -0.55, 0],
        chest: [0.08, -0.2, 0],
        head: [-0.2, 0.5, 0],
        rightUpperArm: [-0.4, -0.3, 0.7],
        leftUpperArm: [0.3, 0.6, 0.6],
        rightLowerArm: [0.3],
        leftLowerArm: [0.5],
        leftUpperLeg: [0.55, 0, 0.2],
        rightUpperLeg: [0.55, 0, 0.2],
        leftLowerLeg: [0.95],
        rightLowerLeg: [0.95],
      },
      root: { hop: -0.12, spin: -0.06 },
    },
    // Launch and saw: the turns at constant speed in the air, moving forward.
    { t: 0.28, ease: 'in', pose: FINALE_SAW, root: { hop: 0.38, lunge: 0.2, spin: 0.75 } },
    { t: 0.4, ease: 'linear', pose: { ...FINALE_SAW, spine: [0.15, 0, -0.12] }, root: { hop: 0.42, lunge: 0.42, spin: 1.5 } },
    // Land: both blades cleave down in front, the second turn completed, deep crouch. Freeze.
    {
      t: 0.5,
      ease: 'snap',
      hold: 0.1,
      pose: {
        spine: [0.7, 0, 0],
        chest: [0.2, 0, 0],
        head: [-0.3, 0, 0],
        rightUpperArm: [0.45, 0.65, 0.15],
        leftUpperArm: [0.45, 0.65, 0.15],
        rightLowerArm: [0.05],
        leftLowerArm: [0.05],
        leftUpperLeg: [0.85, 0, 0.25],
        rightUpperLeg: [0.1, 0, 0.25],
        leftLowerLeg: [1.05],
        rightLowerLeg: [1.2],
        rightFoot: [0.3],
      },
      root: { hop: -0.14, lunge: 0.55, spin: 2 },
    },
    {
      t: 0.74,
      ease: 'out',
      pose: {
        spine: [0.35, 0, 0],
        head: [-0.15, 0, 0],
        rightUpperArm: [0.35, 0.4, 0.3],
        leftUpperArm: [0.35, 0.4, 0.3],
        rightLowerArm: [0.2],
        leftLowerArm: [0.2],
        leftUpperLeg: [0.4, 0, 0.12],
        rightUpperLeg: [0.1, 0, 0.12],
        leftLowerLeg: [0.5],
        rightLowerLeg: [0.55],
      },
      root: { hop: -0.05, lunge: 0.3, spin: 2 },
    },
    // Spin stays on the whole turns (≡ 0): the recovery never unwinds the drill.
    { t: 0.92, ease: 'out', root: { spin: 2 } },
  ],
  trails: [
    both({ from: 0.14, to: 0.56, color: CRIMSON, tail: DARK_CRIMSON, style: 'crescent', width: 1, fade: 0.4, alpha: 0.9 }),
    both({ from: 0.18, to: 0.56, color: BLOOD, tail: CORE, style: 'crescent', width: 0.5, fade: 0.3, alpha: 0.5, blend: 'alpha' }),
    { from: 0.3, to: 0.54, hand: 'right', color: RED_ORANGE, tail: EMBER, style: 'streak', width: 0.4, fade: 0.18, alpha: 0.6 },
  ],
  cues: [
    { t: 0.14, fx: 'dust', at: 'ground', scale: 0.7 },
    ...tips(0.34, 'sparks', 0.5),
    ...tips(0.5, 'slash', 1.2),
    { t: 0.5, fx: 'impact', at: 'ground', scale: 1.2, color: CRIMSON },
    { t: 0.5, fx: 'shockwave', at: 'self', scale: 1.7, color: CRIMSON },
    { t: 0.51, fx: 'cracks', at: 'ground', scale: 1.1, color: DARK_CRIMSON },
    { t: 0.52, fx: 'embers', at: 'ground', scale: 1.0, color: CRIMSON },
    { t: 0.52, fx: 'dust', at: 'ground', scale: 1.0 },
  ],
  hitStop: { t: 0.5, ms: 90, shake: 0.34 },
});

// =============================================================================================

/** Every clip of the kit, by ability id and phase (tests, debug). */
export const BLADEMASTER_KIT: Readonly<Record<string, { strike?: Clip; channel?: Clip }>> = {
  [ABILITY.blademaster.mortalStrike]: { strike: mortalStrike },
  [ABILITY.blademaster.overpower]: { strike: overpower },
  [ABILITY.blademaster.cleave]: { strike: cleave },
  [ABILITY.blademaster.execute]: { strike: execute },
  [ABILITY.blademaster.bladestorm]: { channel: bladestorm },
  [ABILITY.blademaster.colossusSmash]: { strike: colossusSmash },
  [ABILITY.blademaster.pummel]: { strike: pummel },
  [ABILITY.blademaster.dieByTheSword]: { strike: dieByTheSword },
  [ABILITY.blademaster.charge]: { strike: charge },
  [ABILITY.blademaster.slam]: { strike: slam },
  [ABILITY.blademaster.rend]: { strike: rend },
  [ABILITY.blademaster.ravagersFinale]: { strike: ravagersFinale },
};

/** Register every Blademaster ability clip (overrides the reference clips). */
export function registerBlademasterKit(): void {
  for (const [id, clips] of Object.entries(BLADEMASTER_KIT)) registerAbilityClips(id, clips);
}
